import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FileText, CheckCircle2, TrendingUp, Award
} from "lucide-react";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, Cell, Legend
} from "recharts";
import Sidebar from "./Sidebar";
import Header from "./Header";
import ScoreDistribution from "./ScoreDistribution";
import { request } from "../service/HttpService";
import { isRateLimited } from "../service/HttpErrors";

const BUCKET_LABELS = ["0–20", "21–40", "41–60", "61–80", "81–100"];
const DIST_COLORS = ["#e74c3c", "#e67e22", "#f1c40f", "#5b8fa8", "#2a7a5e"];

function StatCard({ icon: Icon, iconBg, iconColor, value, label }) {
    return (
        <div className="submissions-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10, background: iconBg,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
            }}>
                <Icon size={18} color={iconColor} />
            </div>
            <div>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1.1 }}>
                    {value}
                </p>
                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>{label}</p>
            </div>
        </div>
    );
}

function ChartCard({ title, subtitle, children }) {
    return (
        <div className="submissions-card" style={{ padding: "20px" }}>
            <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--ink-900)" }}>{title}</p>
            {subtitle && <p style={{ margin: 0, fontSize: 12, color: "var(--ink-300)", marginTop: 2 }}>{subtitle}</p>}
            <div style={{ marginTop: 16 }}>
                {children}
            </div>
        </div>
    );
}

function formatDate(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function percentileLabel(pct) {
    if (pct == null) return "—";
    if (pct >= 50) return `Top ${100 - pct}%`;
    const suffix = (pct % 10 === 1 && pct !== 11) ? "st"
        : (pct % 10 === 2 && pct !== 12) ? "nd"
        : (pct % 10 === 3 && pct !== 13) ? "rd" : "th";
    return `${pct}${suffix} pct`;
}

const EmptyState = ({ text }) => (
    <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>{text}</p>
);

export default function UserAnalytics() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [data, setData] = useState(null);
    const [dist, setDist] = useState(null);

    useEffect(() => {
        Promise.all([
            request("GET", "/analytics/me"),
            request("GET", "/analytics/distribution"),
        ])
            .then(([me, distribution]) => {
                setData(me);
                setDist(distribution);
                setLoading(false);
            })
            .catch(err => isRateLimited(err) ? setLoadError(err.message) : navigate("/login"));
    }, []);

    if (loading) {
        return (
            <div className="home-layout">
                <Sidebar activePage="Analytics" />
                <main className="home-main">
                    <Header />
                    <div className="home-content">
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>{loadError || "Loading analytics..."}</div>
                    </div>
                </main>
            </div>
        );
    }

    const platform = data.platform ?? {};
    const percentiles = data.percentiles ?? {};

    const noveltyDist = (data.noveltyDistribution ?? []).map((v, i) => ({ range: BUCKET_LABELS[i], count: v }));
    const methodDist = (data.methodDistribution ?? []).map((v, i) => ({ range: BUCKET_LABELS[i], count: v }));
    const impactDist = (data.impactDistribution ?? []).map((v, i) => ({ range: BUCKET_LABELS[i], count: v }));

    // Your Overall Spread — bucketed client-side from each scored paper's overall
    // score (scoreHistory already carries it), matching the backend bucket5 ranges.
    const overallBuckets = [0, 0, 0, 0, 0];
    (data.scoreHistory ?? []).forEach(p => {
        const s = p.overall ?? 0;
        const idx = s <= 20 ? 0 : s <= 40 ? 1 : s <= 60 ? 2 : s <= 80 ? 3 : 4;
        overallBuckets[idx]++;
    });
    const overallDist = overallBuckets.map((count, i) => ({ range: BUCKET_LABELS[i], count }));

    const scoreHistory = (data.scoreHistory ?? []).map(p => ({
        label: formatDate(p.date),
        Novelty: p.novelty ?? 0,
        Methodology: p.method ?? 0,
        Impact: p.impact ?? 0,
        Overall: p.overall ?? 0,
    }));
    const uploads = (data.uploadsByDay ?? []).map(d => ({ ...d, label: formatDate(d.date) }));

    const avgRows = [
        { label: "Novelty", value: data.avgNoveltyScore, platform: platform.avgNoveltyScore, pct: percentiles.novelty, color: "var(--accent-text)" },
        { label: "Methodology", value: data.avgMethodScore, platform: platform.avgMethodScore, pct: percentiles.method, color: "var(--series-blue)" },
        { label: "Impact", value: data.avgImpactScore, platform: platform.avgImpactScore, pct: percentiles.impact, color: "var(--bronze)" },
        { label: "Overall", value: data.avgOverallScore, platform: platform.avgOverallScore, pct: percentiles.overall, color: "var(--brand-text)" },
    ];

    const hasScored = (data.scoredManuscripts ?? 0) > 0;

    const comparison = (hasScored && dist) ? {
        scores: {
            novelty: data.avgNoveltyScore,
            method: data.avgMethodScore,
            impact: data.avgImpactScore,
            overall: data.avgOverallScore,
        },
        percentiles: data.percentiles,
        population: dist.population,
        distribution: {
            novelty: dist.novelty,
            method: dist.method,
            impact: dist.impact,
            overall: dist.overall,
        },
    } : null;

    return (
        <div className="home-layout">
            <Sidebar activePage="Analytics" />
            <main className="home-main">
                <Header />
                <div className="home-content">

                    <div className="upload-page-title-row">
                        <div>
                            <h1 className="upload-page-heading">Your Analytics</h1>
                            <p className="upload-page-meta">Your manuscripts, scores, and how you compare</p>
                        </div>
                    </div>

                    {/* Top-level stats */}
                    <div className="analytics-stats-row" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                        <StatCard icon={FileText} iconBg="var(--accent-soft)" iconColor="#22c55e"
                            value={data.totalManuscripts ?? 0} label="Manuscripts" />
                        <StatCard icon={CheckCircle2} iconBg="var(--blue-bg)" iconColor="var(--blue)"
                            value={data.scoredManuscripts ?? 0} label="Scored" />
                        <StatCard icon={TrendingUp} iconBg="var(--warm-bg)" iconColor="var(--bronze)"
                            value={data.avgOverallScore ?? 0} label="Avg Overall" />
                        <StatCard icon={Award} iconBg="var(--purple-bg)" iconColor="var(--violet)"
                            value={data.bestOverallScore ?? 0} label="Best Overall" />
                    </div>

                    {/* Average scores vs platform + percentile */}
                    <div className="analytics-scores-row">
                        {avgRows.map(s => (
                            <div key={s.label} className="submissions-card" style={{ padding: "16px 20px", textAlign: "center" }}>
                                <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1.1 }}>
                                    {s.value ?? 0}
                                </p>
                                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 4 }}>Avg {s.label}</p>
                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
                                    <p style={{ margin: 0, fontSize: 11, color: "var(--ink-400)" }}>
                                        Platform avg <span style={{ color: "var(--ink-600)", fontWeight: 600 }}>{s.platform ?? 0}</span>
                                    </p>
                                    <p style={{ margin: 0, fontSize: 11.5, fontWeight: 600, color: hasScored ? s.color : "var(--ink-250)", marginTop: 3 }}>
                                        {hasScored ? percentileLabel(s.pct) : "No scores yet"}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* How your papers compare to others (line-only distribution per type) */}
                    {comparison && (
                        <ScoreDistribution
                            data={comparison}
                            title="How your papers compare to others"
                            subtitle="Your average score vs every scored paper on the platform"
                            scoreLabel="Your average"
                        />
                    )}

                    {/* Charts row 2: Your score distributions */}
                    <div className="analytics-charts-row" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                        <ChartCard title="Your Novelty Spread" subtitle="Score spread across your papers">
                            {hasScored ? (
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={noveltyDist} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="range" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Papers">
                                            {noveltyDist.map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyState text="No scored papers yet" />}
                        </ChartCard>

                        <ChartCard title="Your Methodology Spread" subtitle="Score spread across your papers">
                            {hasScored ? (
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={methodDist} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="range" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Papers">
                                            {methodDist.map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyState text="No scored papers yet" />}
                        </ChartCard>

                        <ChartCard title="Your Impact Spread" subtitle="Score spread across your papers">
                            {hasScored ? (
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={impactDist} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="range" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Papers">
                                            {impactDist.map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyState text="No scored papers yet" />}
                        </ChartCard>

                        <ChartCard title="Your Overall Spread" subtitle="Score spread across your papers">
                            {hasScored ? (
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={overallDist} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="range" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Papers">
                                            {overallDist.map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyState text="No scored papers yet" />}
                        </ChartCard>
                    </div>

                    {/* Charts row 3: Score trend + Uploads over time */}
                    <div className="analytics-charts-row">
                        <ChartCard title="Score Trend" subtitle="Your scores over time">
                            {scoreHistory.length === 0 ? (
                                <EmptyState text="No scored papers yet" />
                            ) : (
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={scoreHistory} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
                                        <Line type="monotone" dataKey="Novelty" stroke="var(--accent-text)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                        <Line type="monotone" dataKey="Methodology" stroke="var(--series-blue)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                        <Line type="monotone" dataKey="Impact" stroke="var(--bronze)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                        <Line type="monotone" dataKey="Overall" stroke="var(--brand-text)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard title="Your Uploads" subtitle="Papers you uploaded over time">
                            {uploads.length === 0 ? (
                                <EmptyState text="No uploads yet" />
                            ) : (
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={uploads} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Line type="monotone" dataKey="count" stroke="var(--accent-text)" strokeWidth={2} dot={false} name="Uploads" />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>
                    </div>

                </div>
            </main>
        </div>
    );
}
